import { useCallback, useEffect, useRef, useState } from 'react'
import Sheet from '../../../components/Sheet'
import Loader from '../../../components/ui/Loader'
import { trimVideoOnServer } from '../../../api/images'

/** Потолок отрезка по умолчанию; модель со своим лимитом (Seedance 2.5 — 30 с) передаёт maxSeconds. */
const DEFAULT_MAX_TRIM_DURATION = 15
const MIN_REGION_DURATION = 0.2

function formatTime(s: number): string {
	if (!Number.isFinite(s) || s < 0) return '0.0'
	return s.toFixed(1)
}

interface Props {
	videoUrl: string | null
	onApply: (trimmedUrl: string, duration: number) => void
	onCancel: () => void
	maxSeconds?: number
}

export default function VideoTrimSheet({ videoUrl, onApply, onCancel, maxSeconds }: Props) {
	const MAX_TRIM_DURATION = maxSeconds && maxSeconds > 0 ? maxSeconds : DEFAULT_MAX_TRIM_DURATION
	const open = !!videoUrl
	const videoRef = useRef<HTMLVideoElement | null>(null)
	const playEndTimerRef = useRef<ReturnType<typeof setInterval> | null>(null)

	const [ready, setReady] = useState(false)
	const [loadError, setLoadError] = useState<string | null>(null)
	const [isPlaying, setIsPlaying] = useState(false)
	const [duration, setDuration] = useState(0)
	const [regionStart, setRegionStart] = useState(0)
	const [regionEnd, setRegionEnd] = useState(MAX_TRIM_DURATION)
	const [applying, setApplying] = useState(false)

	useEffect(() => {
		if (!open) return
		// queueMicrotask — конвенция репо против set-state-in-effect (каскадные рендеры), как в AudioTrimSheet
		queueMicrotask(() => {
			setReady(false)
			setLoadError(null)
			setIsPlaying(false)
			setApplying(false)
			setRegionStart(0)
			setRegionEnd(MAX_TRIM_DURATION)
		})
		return () => {
			if (playEndTimerRef.current) {
				clearInterval(playEndTimerRef.current)
				playEndTimerRef.current = null
			}
		}
	}, [open, videoUrl, MAX_TRIM_DURATION])

	const handleLoadedMetadata = useCallback(() => {
		const v = videoRef.current
		if (!v) return
		setDuration(v.duration)
		setRegionStart(0)
		setRegionEnd(Math.min(MAX_TRIM_DURATION, v.duration))
		setReady(true)
	}, [MAX_TRIM_DURATION])

	const onStartChange = useCallback(
		(newStart: number) => {
			const s = Math.max(0, Math.min(newStart, duration))
			const e = Math.min(s + MAX_TRIM_DURATION, duration)
			setRegionStart(s)
			setRegionEnd(e)
		},
		[duration, MAX_TRIM_DURATION],
	)

	const onEndChange = useCallback(
		(newEnd: number) => {
			const maxEnd = Math.min(regionStart + MAX_TRIM_DURATION, duration)
			const minEnd = regionStart + MIN_REGION_DURATION
			const e = Math.max(minEnd, Math.min(newEnd, maxEnd))
			setRegionEnd(e)
		},
		[regionStart, duration, MAX_TRIM_DURATION],
	)

	const handlePlayRegion = useCallback(() => {
		const v = videoRef.current
		if (!v) return
		if (playEndTimerRef.current) {
			clearInterval(playEndTimerRef.current)
			playEndTimerRef.current = null
		}
		v.currentTime = regionStart
		v.play()
			.then(() => setIsPlaying(true))
			.catch(() => setIsPlaying(false))
		playEndTimerRef.current = setInterval(() => {
			if (!videoRef.current) return
			if (videoRef.current.currentTime >= regionEnd) {
				videoRef.current.pause()
				setIsPlaying(false)
				if (playEndTimerRef.current) {
					clearInterval(playEndTimerRef.current)
					playEndTimerRef.current = null
				}
			}
		}, 80)
	}, [regionStart, regionEnd])

	const handlePause = useCallback(() => {
		const v = videoRef.current
		if (!v) return
		v.pause()
		setIsPlaying(false)
		if (playEndTimerRef.current) {
			clearInterval(playEndTimerRef.current)
			playEndTimerRef.current = null
		}
	}, [])

	const handleApply = useCallback(async () => {
		if (!videoUrl || applying) return
		setApplying(true)
		try {
			const res = await trimVideoOnServer(videoUrl, regionStart, regionEnd)
			onApply(res.url, regionEnd - regionStart)
		} catch (err) {
			setLoadError(err instanceof Error ? err.message : 'Ошибка обрезки видео')
			setApplying(false)
		}
	}, [videoUrl, regionStart, regionEnd, applying, onApply])

	const regionDur = Math.max(0, regionEnd - regionStart)
	const canSubmit = ready && !applying && regionDur >= MIN_REGION_DURATION && regionDur <= MAX_TRIM_DURATION

	const startPct = duration > 0 ? (regionStart / duration) * 100 : 0
	const widthPct = duration > 0 ? (regionDur / duration) * 100 : 0

	return (
		<Sheet
			open={open}
			onClose={onCancel}
			variant="center"
			title={`Видео длиннее ${MAX_TRIM_DURATION} секунд — выберите отрезок`}
			className="suno-replace-sheet"
		>
			<div className="suno-replace-sheet__body">
				<div className="suno-replace-sheet__waveform-wrap" style={{ position: 'relative' }}>
					{!ready && !loadError && videoUrl && (
						<div className="suno-replace-sheet__wave-loader">
							<Loader size="md" centered label="Загружаем видео..." />
						</div>
					)}
					{loadError && <div className="suno-replace-sheet__error">{loadError}</div>}
					{videoUrl && (
						<video
							ref={videoRef}
							src={videoUrl}
							onLoadedMetadata={handleLoadedMetadata}
							onError={() => setLoadError('Не удалось загрузить видео')}
							playsInline
							preload="metadata"
							style={{ width: '100%', maxHeight: 300, background: '#000' }}
						/>
					)}
					{ready && duration > 0 && (
						<div
							style={{
								position: 'absolute',
								top: 0,
								bottom: 0,
								left: `${startPct}%`,
								width: `${widthPct}%`,
								background: 'rgba(139, 92, 246, 0.18)',
								borderLeft: '2px solid rgba(139, 92, 246, 0.85)',
								borderRight: '2px solid rgba(139, 92, 246, 0.85)',
								pointerEvents: 'none',
							}}
						/>
					)}
				</div>

				{ready && duration > 0 && (
					<div style={{ display: 'flex', flexDirection: 'column', gap: 6, marginTop: 8 }}>
						<label style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
							<span style={{ width: 70, fontSize: 12, color: '#8e97ad' }}>Начало</span>
							<input
								type="range"
								min={0}
								max={duration}
								step={0.1}
								value={regionStart}
								onChange={(e) => onStartChange(parseFloat(e.target.value))}
								style={{ flex: 1, accentColor: 'rgb(139,92,246)' }}
							/>
							<span style={{ width: 50, fontSize: 12, textAlign: 'right' }}>{formatTime(regionStart)}с</span>
						</label>
						<label style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
							<span style={{ width: 70, fontSize: 12, color: '#8e97ad' }}>Конец</span>
							<input
								type="range"
								min={regionStart + MIN_REGION_DURATION}
								max={Math.min(regionStart + MAX_TRIM_DURATION, duration)}
								step={0.1}
								value={regionEnd}
								onChange={(e) => onEndChange(parseFloat(e.target.value))}
								style={{ flex: 1, accentColor: 'rgb(139,92,246)' }}
							/>
							<span style={{ width: 50, fontSize: 12, textAlign: 'right' }}>{formatTime(regionEnd)}с</span>
						</label>
					</div>
				)}

				<div className="suno-replace-sheet__controls">
					<button
						type="button"
						className="suno-replace-sheet__ctrl-btn suno-replace-sheet__ctrl-btn--region"
						onClick={isPlaying ? handlePause : handlePlayRegion}
						disabled={!ready}
					>
						{isPlaying ? 'Пауза' : 'Играть выделенное'}
					</button>
					<div className="suno-replace-sheet__region-info">
						<span className="suno-replace-sheet__region-time">
							Выбрано: {formatTime(regionStart)} – {formatTime(regionEnd)} сек ({formatTime(regionDur)} сек)
						</span>
						<span className="suno-replace-sheet__region-dur">
							всего {formatTime(duration)} сек · максимум {MAX_TRIM_DURATION} сек
						</span>
					</div>
				</div>

				<div className="suno-replace-sheet__footer">
					<button
						type="button"
						className="suno-replace-sheet__cancel"
						onClick={onCancel}
						disabled={applying}
					>
						Отмена
					</button>
					<button
						type="button"
						className="suno-replace-sheet__submit"
						onClick={handleApply}
						disabled={!canSubmit}
					>
						{applying ? 'Обрезаем...' : 'Применить'}
					</button>
				</div>
			</div>
		</Sheet>
	)
}
